import React, { useMemo, useState } from "react";
import "../index.scss";
import NfcCardIcon from "../../../img/id_card.svg";
import BadgeIdCardIcon from "../../../img/badge_nid.svg";
import UserGroupIcon from "../../../img/users.svg";
import PhoneIcon from "../../../img/phone.svg";
import UserAvatarIcon from "../../../img/user_avatar.svg";
import MailIcon from "../../../img/mail.svg";
import UserSecuredIcon from "../../../img/user_secured.svg";
import CalendarMonthIcon from "../../../img/calendar_month.svg";
import MapPinIcon from "../../../img/map_pin.svg";
import {
  getAssignmentFieldName,
  getTaggingText,
} from "../../UserList/utils/userHelper";
import {
  CS_USERS,
  LEAVE_DAYS_OPTIONS,
  MANAGER_TYPE_OPTIONS,
  USER_ROLES,
} from "../../UserList/utils/userConfigs";
import { userService } from "../../UserList/services/userService";
import { useNotification } from "../../Notifications/NotificationProvider";

const UserBasicInfoCard = ({
  user,
  canUnregisterFacematch = true,
  isOwnProfile = false,
}) => {
  const dispatchNotification = useNotification();
  const [isUnregisteringFacematch, setIsUnregisteringFacematch] =
    useState(false);

  const handleUnregisterFacematching = async () => {
    const employeeId = user?.employee_id || user?.employee_code;

    if (!employeeId) {
      dispatchNotification({
        type: "ERROR",
        message: "Employee ID not found for this user",
      });
      return;
    }

    try {
      setIsUnregisteringFacematch(true);
      await userService.unregisterFacematching(employeeId);
      dispatchNotification({
        type: "SUCCESS",
        message: "Facematching unregistered successfully",
      });
    } catch (error) {
      dispatchNotification({
        type: "ERROR",
        message:
          error?.response?.data?.message || "Failed to unregister facematching",
      });
    } finally {
      setIsUnregisteringFacematch(false);
    }
  };

  const transformedUserDetails = useMemo(() => {
    const userType = user.user_type;
    const assignmentField = getAssignmentFieldName(userType);
    const assignedTo = getTaggingText([
      ...(user[assignmentField]?.map((item) => item.name) || []),
    ]);
    const isCsUser = CS_USERS.includes(userType);
    let userTypeLabel =
      USER_ROLES.find((role) => role.value === user.user_type)?.name || "-";
    let userCategory = "-";
    if (isCsUser) {
      userCategory = userTypeLabel;
      userTypeLabel = "Customer Support";
    } else if (MANAGER_TYPE_OPTIONS[userType]) {
      userCategory =
        MANAGER_TYPE_OPTIONS[userType].find(
          (option) => option.value === user.user_category
        )?.label || "-";
    }

    const leaveDayLabel =
      LEAVE_DAYS_OPTIONS.find((option) => option.value === user.leave_day)
        ?.label || "-";

    return [
      {
        icon: BadgeIdCardIcon,
        label: "Name",
        value: user.name || "-",
      },
      {
        icon: PhoneIcon,
        label: "Contact",
        value: user.contact_number || "-",
      },
      {
        icon: UserGroupIcon,
        label: "Username",
        value: user.username || "-",
      },
      {
        icon: MailIcon,
        label: "Email",
        value: user.email || "-",
      },
      {
        icon: UserGroupIcon,
        label: "Role",
        value: userTypeLabel,
      },
      {
        icon: UserSecuredIcon,
        label: "User Type",
        value: userCategory,
      },
      {
        icon: CalendarMonthIcon,
        label: "Leave Days",
        value: leaveDayLabel,
      },
      {
        icon: MapPinIcon,
        label: "Assigned To",
        value: assignedTo || "-",
        truncate: true,
      },
    ];
  }, [user]);

  if (!user) {
    return <div className="user-info-card">No user data available</div>;
  }

  return (
    <div className="user-info-card">
      <div className="user-info-header">
        <span className="user-info-title">
          {isOwnProfile ? "Your Profile" : `${user.name}'s Profile`}
        </span>
        {canUnregisterFacematch && (
          <button
            className="unregister-facematching-btn"
            onClick={handleUnregisterFacematching}
            disabled={isUnregisteringFacematch}
          >
            <span className="unregister-facematching-text">
              Unregister Facematching
            </span>
            {isUnregisteringFacematch ? (
              <span className="unregister-facematching-loader" />
            ) : (
              <img src={NfcCardIcon} alt="Unregister Facematching Icon" />
            )}
          </button>
        )}
      </div>
      <hr className="user-info-divider" />
      <div className="user-info-content">
        <div className="user-avatar-container">
          <img
            src={
              user.image && user.image !== "null" ? user.image : UserAvatarIcon
            }
            alt="User Avatar"
          />
        </div>
        <div className="user-details">
          <div className="user-details-column">
            {transformedUserDetails.slice(0, 4).map((detail, index) => (
              <UserDetailsRow
                key={index}
                icon={detail.icon}
                label={detail.label}
                value={detail.value}
                truncate={detail.truncate}
              />
            ))}
          </div>
          <div className="user-details-column-divider" />
          <div className="user-details-column">
            {transformedUserDetails.slice(4).map((detail, index) => (
              <UserDetailsRow
                key={index}
                icon={detail.icon}
                label={detail.label}
                value={detail.value}
                truncate={detail.truncate}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const TRUNCATE_CHARS = 30;

const UserDetailsRow = ({ icon, label, value, truncate = false }) => {
  const [expanded, setExpanded] = useState(false);

  const isTruncatable = truncate && value && value.length > TRUNCATE_CHARS;
  const displayValue =
    isTruncatable && !expanded
      ? value.slice(0, TRUNCATE_CHARS).trimEnd() + "\u2026"
      : value;

  return (
    <div className="user-detail-row">
      <div className="user-details-label">
        <img src={icon} alt={`${label} Icon`} className="detail-icon" />
        <span>{label}</span>
      </div>
      <div className="user-details-value-wrapper">
        <div className="user-details-value">
          {displayValue}
          {isTruncatable && (
            <button
              type="button"
              className="user-details-see-more"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? " See less" : " See more"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserBasicInfoCard;
